import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Zap,
  TrendingUp,
  MessageCircle,
  Target,
  Rocket,
  ArrowRight,
  CheckCircle2,
  Users,
  Globe,
  BarChart3,
  Shield,
  Clock,
  Star,
  Quote,
  Rewind,
  Bot,
  Sparkle,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { GhostWidgetPopup } from "@/components/ghost/GhostWidgetPopup";

const Landing = () => {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Add smooth parallax effect
    const handleScroll = () => {
      if (heroRef.current) {
        const scrolled = window.scrollY;
        heroRef.current.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const feedbackBubbles = [
    "I love this feature!",
    "Dark mode please!",
    "When is the mobile app coming?",
    "This saved us hours!",
    "Best feedback tool ever!",
    "Can we get API access?",
    "Amazing support team!",
    "Please add webhooks",
  ];

  const testimonials = [
    {
      quote:
        "Feednatic transformed how we collect user feedback. The AI chat feature alone saved us 20 hours a week.",
      author: "Sarah Chen",
      role: "Product Manager at TechCorp",
      rating: 5,
    },
    {
      quote:
        "The RICE scoring system helped us prioritize our roadmap perfectly. We're building exactly what users want.",
      author: "Mike Rodriguez",
      role: "CEO at StartupXYZ",
      rating: 5,
    },
    {
      quote:
        "Custom domains and white-labeling made it feel like our own platform. Our users love the feedback portal.",
      author: "Jessica Taylor",
      role: "Head of Product at SaaS Inc",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden">
      <GhostWidgetPopup />
      {/* Animated Background Grid */}
      <div className="fixed inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />

      {/* Gradient Orbs */}
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-purple-600/30 rounded-full blur-[128px] animate-pulse" />
      <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-blue-600/30 rounded-full blur-[128px] animate-pulse delay-700" />

      <div className="relative z-10">
        {/* Navigation */}
        <nav className="border-b border-white/10 backdrop-blur-xl bg-black/40">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Rewind className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Feednatic
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-white hover:text-white hover:bg-white/10"
                onClick={() => navigate("/auth")}
              >
                Sign In
              </Button>
              <Button
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0 shadow-[0_0_20px_rgba(147,51,234,0.3)]"
                onClick={() => navigate("/auth?mode=signup")}
              >
                Start Free
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden">
          <div ref={heroRef} className="max-w-6xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm">
              <span className="text-sm text-purple-300">
                ✨ Setup in 2 minutes
              </span>
            </div>

            <h1 className="text-6xl md:text-8xl font-black mb-6 leading-tight">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                Stop Guessing.
              </span>
              <br />
              <span className="text-white">Build What Users</span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Actually Crave.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto">
              The all-in-one feedback hub powered by meaningful AI.
            </p>

            <div className="flex items-center justify-center gap-4 mb-16">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-lg px-8 py-6 rounded-xl border-0 shadow-[0_0_40px_rgba(147,51,234,0.4)] hover:shadow-[0_0_60px_rgba(147,51,234,0.6)] transition-all duration-300"
                onClick={() => navigate("/auth?mode=signup")}
              >
                Start Free
                <Sparkles className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 bg-white/5 text-white hover:bg-white/10 hover:text-white text-lg px-8 py-6 rounded-xl backdrop-blur-sm"
                onClick={() => navigate("/feednatic")}
              >
                View Demo
              </Button>
            </div>

            {/* 3D Floating Interface Preview */}
            <div className="relative mx-auto max-w-4xl perspective-1000">
              <div className="relative transform rotate-y-12 rotate-x-6 transition-transform duration-700 hover:rotate-y-0 hover:rotate-x-0">
                <Card className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
                  <div className="p-8">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="space-y-3">
                      <div className="h-4 bg-gradient-to-r from-purple-500/40 to-transparent rounded w-3/4" />
                      <div className="h-4 bg-gradient-to-r from-blue-500/40 to-transparent rounded w-1/2" />
                      <div className="h-4 bg-gradient-to-r from-cyan-500/40 to-transparent rounded w-5/6" />
                      <div className="mt-6 grid grid-cols-3 gap-4">
                        <div className="h-24 bg-gradient-to-br from-purple-600/20 to-purple-600/5 border border-purple-500/30 rounded-lg" />
                        <div className="h-24 bg-gradient-to-br from-blue-600/20 to-blue-600/5 border border-blue-500/30 rounded-lg" />
                        <div className="h-24 bg-gradient-to-br from-cyan-600/20 to-cyan-600/5 border border-cyan-500/30 rounded-lg" />
                      </div>
                    </div>
                  </div>
                </Card>
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 blur-3xl -z-10" />
              </div>
            </div>
          </div>
        </section>

        {/* Scrolling Feedback Ticker */}
        <section className="py-12 overflow-hidden border-y border-white/10 bg-black/40 backdrop-blur-sm">
          <div className="flex animate-scroll whitespace-nowrap">
            {[...feedbackBubbles, ...feedbackBubbles, ...feedbackBubbles].map(
              (feedback, idx) => (
                <div
                  key={idx}
                  className="inline-block mx-4 px-6 py-3 rounded-full bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 backdrop-blur-sm"
                >
                  <span className="text-white/80">{feedback}</span>
                </div>
              )
            )}
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                  10K+
                </div>
                <div className="text-white/60">Feedback Items Collected</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  50+
                </div>
                <div className="text-white/60">Happy Companies</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  99.9%
                </div>
                <div className="text-white/60">Uptime Guarantee</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent mb-2">
                  2 min
                </div>
                <div className="text-white/60">Setup Time</div>
              </div>
            </div>
          </div>
        </section>

        {/* Bento Grid Features */}
        <section className="py-32 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Everything You Need
              </h2>
              <p className="text-xl text-white/80">
                Powerful features that actually matter
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[280px]">
              {/* Large Card - AI Live Chat */}
              <Card className="md:col-span-2 bg-gradient-to-br from-purple-600/20 to-purple-600/5 backdrop-blur-xl border border-purple-500/30 overflow-hidden group hover:border-purple-500/50 transition-all duration-300 shadow-[0_8px_32px_rgba(147,51,234,0.1)] hover:shadow-[0_8px_32px_rgba(147,51,234,0.2)]">
                <div className="p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-700 to-purple-900 flex items-center justify-center mb-4 shadow-lg">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-[#0a0a0a]">
                      AI Live Chat
                    </h3>
                    <p className="text-[#0a0a0a]/80 text-lg">
                      Instant answers powered by your knowledge base. Zero
                      training required.
                    </p>
                  </div>
                  <div className="mt-4 p-4 rounded-xl bg-black/30 border border-purple-500/20">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="h-3 bg-gradient-to-r from-purple-500/60 to-transparent rounded w-3/4 mb-2" />
                        <div className="h-3 bg-gradient-to-r from-blue-500/60 to-transparent rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Tall Card - Smart Prioritization */}
              <Card className="md:row-span-2 bg-gradient-to-br from-blue-600/20 to-blue-600/5 backdrop-blur-xl border border-blue-500/30 overflow-hidden group hover:border-blue-500/50 transition-all duration-300 shadow-[0_8px_32px_rgba(59,130,246,0.1)] hover:shadow-[0_8px_32px_rgba(59,130,246,0.2)]">
                <div className="p-8 h-full flex flex-col">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-700 to-blue-900 flex items-center justify-center mb-4 shadow-lg">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-[#0a0a0a]">
                      Smart Prioritization
                    </h3>
                    <p className="text-[#0a0a0a]/80">
                      RICE scoring built-in. Know what to build next.
                    </p>
                  </div>
                  <div className="flex-1 flex items-center justify-center mt-8">
                    <div className="w-full space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#0a0a0a]/70">Reach</span>
                        <div className="h-2 flex-1 mx-4 bg-blue-500/20 rounded-full overflow-hidden">
                          <div className="h-full w-4/5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
                        </div>
                        <span className="text-sm font-bold text-[#0a0a0a]">
                          85
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#0a0a0a]/70">
                          Impact
                        </span>
                        <div className="h-2 flex-1 mx-4 bg-blue-500/20 rounded-full overflow-hidden">
                          <div className="h-full w-3/5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
                        </div>
                        <span className="text-sm font-bold text-[#0a0a0a]">
                          62
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#0a0a0a]/70">
                          Confidence
                        </span>
                        <div className="h-2 flex-1 mx-4 bg-blue-500/20 rounded-full overflow-hidden">
                          <div className="h-full w-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
                        </div>
                        <span className="text-sm font-bold text-[#0a0a0a]">
                          95
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#0a0a0a]/70">
                          Effort
                        </span>
                        <div className="h-2 flex-1 mx-4 bg-blue-500/20 rounded-full overflow-hidden">
                          <div className="h-full w-2/5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
                        </div>
                        <span className="text-sm font-bold text-[#0a0a0a]">
                          42
                        </span>
                      </div>
                      <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border border-blue-500/40">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[#0a0a0a]/90">
                            RICE Score
                          </span>
                          <span className="text-3xl font-black text-[#0a0a0a]">
                            87.3
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Small Card - Roadmap */}
              <Card className="bg-gradient-to-br from-cyan-600/20 to-cyan-600/5 backdrop-blur-xl border border-cyan-500/30 overflow-hidden group hover:border-cyan-500/50 transition-all duration-300 shadow-[0_8px_32px_rgba(6,182,212,0.1)] hover:shadow-[0_8px_32px_rgba(6,182,212,0.2)]">
                <div className="p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-700 to-cyan-900 flex items-center justify-center mb-4 shadow-lg">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2 text-[#0a0a0a]">
                      Public Roadmap
                    </h3>
                    <p className="text-[#0a0a0a]/80 text-sm">
                      Keep users in the loop
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500" />
                      <div className="flex-1 h-2 bg-yellow-500/20 rounded" />
                      <span className="text-xs text-[#0a0a0a]/70">
                        In Progress
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <div className="flex-1 h-2 bg-green-500/20 rounded" />
                      <span className="text-xs text-[#0a0a0a]/70">Done</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Medium Card - Affordability */}
              <Card className="md:col-span-2 bg-gradient-to-br from-green-600/20 to-green-600/5 backdrop-blur-xl border border-green-500/30 overflow-hidden group hover:border-green-500/50 transition-all duration-300 shadow-[0_8px_32px_rgba(34,197,94,0.1)] hover:shadow-[0_8px_32px_rgba(34,197,94,0.2)]">
                <div className="p-8 h-full flex items-center justify-between">
                  <div className="flex-1">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-700 to-green-900 flex items-center justify-center mb-4 shadow-lg">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-[#0a0a0a]">
                      30% Cheaper
                    </h3>
                    <p className="text-[#0a0a0a]/80 text-lg">
                      More features. Less cost. Simple math.
                    </p>
                  </div>
                  <div className="flex-1 flex items-center justify-end">
                    <div className="relative w-48 h-48">
                      <svg className="transform -rotate-90 w-48 h-48">
                        <circle
                          cx="96"
                          cy="96"
                          r="80"
                          stroke="currentColor"
                          strokeWidth="12"
                          fill="transparent"
                          className="text-white/10"
                        />
                        <circle
                          cx="96"
                          cy="96"
                          r="80"
                          stroke="url(#gradient)"
                          strokeWidth="12"
                          fill="transparent"
                          strokeDasharray="502"
                          strokeDashoffset="150"
                          className="transition-all duration-1000"
                        />
                        <defs>
                          <linearGradient
                            id="gradient"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="100%"
                          >
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="100%" stopColor="#059669" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl font-black text-green-400">
                            70%
                          </div>
                          <div className="text-xs text-white/60">
                            Efficiency
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Testimonials Section
        <section className="py-32 px-6 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Loved by Product Teams
              </h2>
              <p className="text-xl text-white/80">
                See what our customers have to say
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, idx) => (
                <Card
                  key={idx}
                  className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-xl border border-white/20 p-8 hover:border-purple-500/50 transition-all duration-300"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-purple-600 mb-4" />
                  <p className="text-[#0a0a0a]/90 mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <div className="font-bold text-[#0a0a0a]">
                      {testimonial.author}
                    </div>
                    <div className="text-sm text-[#0a0a0a]/60">
                      {testimonial.role}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section> */}

        {/* Feature Highlights Section */}
        <section className="py-32 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block px-4 py-2 rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm mb-6">
                  <span className="text-sm text-purple-300">
                    Enterprise Ready
                  </span>
                </div>
                <h2 className="text-5xl font-black mb-6 text-white">
                  White-Label Your Feedback Portal
                </h2>
                <p className="text-xl text-white/80 mb-8">
                  Use custom domains and remove our branding. Make it truly
                  yours.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Globe className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-white mb-1">
                        Custom Domains
                      </div>
                      <div className="text-white/70">
                        feedback.yourbrand.com
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-white mb-1">
                        Your Branding
                      </div>
                      <div className="text-white/70">
                        Logo, colors, and styling
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-white mb-1">
                        Team Collaboration
                      </div>
                      <div className="text-white/70">
                        Unlimited team members on Enterprise
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <Card className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 p-8">
                  <div className="space-y-4">
                    <div className="h-12 bg-gradient-to-r from-purple-500/40 to-transparent rounded" />
                    <div className="h-32 bg-gradient-to-r from-blue-500/30 to-transparent rounded" />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="h-24 bg-gradient-to-br from-cyan-500/30 to-transparent rounded" />
                      <div className="h-24 bg-gradient-to-br from-green-500/30 to-transparent rounded" />
                    </div>
                  </div>
                </Card>
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 blur-3xl -z-10" />
              </div>
            </div>
          </div>
        </section>

        {/* AI Live Chat Section */}
        <section className="py-32 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="relative order-2 md:order-1">
                <Card className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 p-8">
                  <div className="space-y-4">
                    {/* Chat interface mockup */}
                    <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-700 to-purple-900 flex items-center justify-center">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-black">
                          AI Assistant
                        </div>
                        <div className="text-xs text-green-400">Online</div>
                      </div>
                    </div>

                    {/* Chat messages */}
                    <div className="space-y-3">
                      <div className="flex justify-start">
                        <div className="bg-[#1a1a1a] backdrop-blur-sm rounded-2xl rounded-tl-none px-4 py-2 max-w-[80%] border border-white/20">
                          <p className="text-sm text-white font-medium">
                            How do I reset my password?
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl rounded-tr-none px-4 py-2 max-w-[80%]">
                          <p className="text-sm text-white font-medium">
                            I can help you with that! You can reset your
                            password by clicking on "Forgot Password" on the
                            login page...
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="bg-[#1a1a1a] backdrop-blur-sm rounded-2xl rounded-tl-none px-4 py-2 max-w-[80%] border border-white/20">
                          <p className="text-sm text-white font-medium">
                            Thanks! That worked perfectly.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Typing indicator */}
                    <div className="flex items-center gap-2 text-black/90 text-xs">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-75" />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-150" />
                      </div>
                      <span>AI is analyzing your knowledge base...</span>
                    </div>
                  </div>
                </Card>
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 blur-3xl -z-10" />
              </div>

              <div className="order-1 md:order-2">
                <div className="inline-block px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 backdrop-blur-sm mb-6">
                  <span className="text-sm text-blue-300">🤖 AI-Powered</span>
                </div>
                <h2 className="text-5xl font-black mb-6 text-white">
                  AI Live Chat Support
                </h2>
                <p className="text-xl text-white/80 mb-8">
                  Let AI handle customer questions 24/7 using your knowledge
                  base. No training required.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Bot className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-white mb-1">
                        Instant Responses
                      </div>
                      <div className="text-white/70">
                        AI answers customer questions in real-time based on your
                        knowledge base
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Sparkle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-white mb-1">
                        Smart Learning
                      </div>
                      <div className="text-white/70">
                        Automatically improves responses as your knowledge base
                        grows
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-white mb-1">
                        24/7 Availability
                      </div>
                      <div className="text-white/70">
                        Never miss a customer question, even outside business
                        hours
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-32 px-6 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Simple, Honest Pricing
              </h2>
              <p className="text-xl text-white/80">
                No hidden fees. No surprises. Just great value.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Free Plan */}
              <Card className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-xl border border-white/20 p-8 hover:border-white/40 transition-all duration-300 flex flex-col">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#0a0a0a] mb-2">
                    Free Plan
                  </h3>
                  <div className="text-5xl font-black text-[#0a0a0a] mb-2">
                    $0
                  </div>
                  <p className="text-[#0a0a0a]/70">Forever free</p>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Up to 10 feedback items
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">1 Feedback Board</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">1 Roadmap</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Custom Feedback Page
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">1 team member</span>
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="w-full border-[#0a0a0a]/30 bg-[#0a0a0a]/5 text-[#0a0a0a] hover:bg-[#0a0a0a]/10 mt-auto"
                  onClick={() => navigate("/auth?mode=signup")}
                >
                  Get Started
                </Button>
              </Card>

              {/* Starter Plan - Highlighted */}
              <Card className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-xl border-2 border-purple-500 p-8 relative overflow-hidden shadow-[0_0_50px_rgba(147,51,234,0.3)] animate-glow flex flex-col">
                <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                  POPULAR
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#0a0a0a] mb-2">
                    Starter Plan
                  </h3>
                  <div className="text-5xl font-black text-[#0a0a0a] mb-2">
                    $49
                  </div>
                  <p className="text-[#0a0a0a]/80">Per month</p>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      AI Live Chat Integration
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Unlimited feedback items
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Up to 3 boards & roadmaps
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">Custom domains</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">Your Branding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Up to 3 team members
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Unlimited changelogs
                    </span>
                  </li>
                </ul>
                <Button
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 border-0 text-white mt-auto"
                  onClick={() => navigate("/auth?mode=signup")}
                >
                  Start Free Trial
                  <Rocket className="ml-2 w-4 h-4" />
                </Button>
              </Card>

              {/* Enterprise Plan */}
              <Card className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-xl border border-white/20 p-8 hover:border-white/40 transition-all duration-300 flex flex-col">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#0a0a0a] mb-2">
                    Enterprise Plan
                  </h3>
                  <div className="text-5xl font-black text-[#0a0a0a] mb-2">
                    $79
                  </div>
                  <p className="text-[#0a0a0a]/70">Per month</p>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Everything in Starter
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Unlimited boards & roadmaps
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      Up to 5 team members
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">Priority support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-[#0a0a0a]/90">
                      +$10/month per additional member
                    </span>
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="w-full border-[#0a0a0a]/30 bg-[#0a0a0a]/5 text-[#0a0a0a] hover:bg-[#0a0a0a]/10 mt-auto"
                  onClick={() => navigate("/auth?mode=signup")}
                >
                  Get Started
                </Button>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-32 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Ready to Build Better Products?
            </h2>
            <p className="text-xl text-white/80 mb-12">
              Join hundreds of product teams using Feednatic to collect,
              prioritize, and act on user feedback.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-lg px-12 py-6 rounded-xl border-0 shadow-[0_0_40px_rgba(147,51,234,0.4)] hover:shadow-[0_0_60px_rgba(147,51,234,0.6)] transition-all duration-300"
              onClick={() => navigate("/auth?mode=signup")}
            >
              Start Free Today
              <Sparkles className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-12 px-6 bg-black/40 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-white/60 text-lg mb-4">
              Built for product teams who care.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-white/60">
              <button
                onClick={() => navigate("/privacy-policy")}
                className="hover:text-white transition-colors"
              >
                Privacy
              </button>
              <button
                onClick={() => navigate("/terms-of-service")}
                className="hover:text-white transition-colors"
              >
                Terms
              </button>
              <button
                onClick={() => navigate("/blog")}
                className="hover:text-white transition-colors"
              >
                Blog
              </button>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }

        .animate-scroll {
          animation: scroll 30s linear infinite;
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 50px rgba(147, 51, 234, 0.3);
          }
          50% {
            box-shadow: 0 0 80px rgba(147, 51, 234, 0.5);
          }
        }

        .animate-glow {
          animation: glow 3s ease-in-out infinite;
        }

        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }

        .perspective-1000 {
          perspective: 1000px;
        }

        .rotate-y-12 {
          transform: rotateY(12deg);
        }

        .rotate-x-6 {
          transform: rotateX(6deg);
        }
      `}</style>
    </div>
  );
};

export default Landing;
